import { useState, useEffect } from "react";
import Icon from "../Icon/Icon";
import Button from "../Button/Button";
import Modal from "../Modal/Modal";
import InviteUserForm from "../Forms/InviteUserForm";
import DeleteListForm from "../Forms/DeleteListForm";
import { useSharedLists } from "../../context/SharedListContext";
import { useAuth } from "../../context/AuthContext";
import ListPreviewCollage from "../ListPreviewCollage/ListPreviewCollage";

export default function ListCard({ list, onSelect, onEdit, onDelete }) {
    const { inviteUser } = useSharedLists();
    const { user } = useAuth();
    const [isInviteModalOpen, setInviteModalOpen] = useState(false);
    const [deleteListModalOpen, setDeleteListModalOpen] = useState(false);
    const [assignedRecipient, setAssignedRecipient] = useState(null);

    const thumbnails = list.memberInfo?.slice(0, 5) || [];
    const remainingCount = (list.memberInfo?.length || 0) - thumbnails.length;

    const isAdmin = user?.uid === list.ownerId;

    useEffect(() => {
        if (!user || !list?.participants) {
            setAssignedRecipient(null);
            return;
        }

        let participant;

        if (user.uid) {
            participant = list.participants.find(p => p.userId === user.uid);
        }

        if (!participant) {
            const userName = user.displayName || user.email?.split("@")[0];
            participant = list.participants.find(p => !p.userId && p.name === userName);
        }

        if (participant?.assignedTo) {
            setAssignedRecipient({ name: participant.assignedTo });
        } else {
            setAssignedRecipient(null);
        }
    }, [list, user]);

    return (
        <>
            <div
                onClick={() => onSelect && onSelect(list)}
                className="bg-white shadow rounded-lg p-4 flex flex-col sm:flex-row items-stretch gap-4 cursor-pointer hover:shadow-md transition-colors"
            >
                {/* Left: Image Preview */}
                <div className="w-full sm:w-1/4 rounded overflow-hidden relative h-[160px] sm:h-[134px]">
                    <ListPreviewCollage items={list.items} />
                </div>

                {/* Middle: List Info */}
                <div className="w-full sm:w-1/2 flex flex-col justify-between">
                    <div className="flex items-center gap-2 items-center">
                        <h2 className="text-lg font-bold">{list.name}</h2>
                        {list.isSecretSanta && (
                            <span className="text-sm text-red-600 px-2 py-1 border border-red-200 rounded">
                                Secret Santa
                            </span>
                        )}
                    </div>

                    {/* Secret Santa Assignment Box */}
                    {list.isSecretSanta && assignedRecipient && (
                        <div className="my-2 text-md">
                            You're Gifting: <strong>{assignedRecipient.name}</strong>
                        </div>
                    )}

                    {list.description && <p className="mb-3 text-sm sm:text-base">{list.description}</p>}

                    {/* Member Avatars */}
                    {!list.isSecretSanta && thumbnails.length > 0 && (
                        <div className="flex items-center mb-2 flex-wrap">
                            {thumbnails.map((member, index) => {
                                const displayName = member.displayName || "Member";
                                const initial = displayName.charAt(0).toUpperCase();

                                return (
                                    <div
                                        key={`${list.id}-${member.uid || index}`}
                                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white bg-surface flex items-center justify-center text-sm font-medium ${index !== 0 ? "-ml-2" : ""
                                            }`}
                                        title={displayName}
                                    >
                                        {initial}
                                    </div>
                                );
                            })}

                            {remainingCount > 0 && (
                                <span className="ml-2 font-medium text-sm sm:text-base">
                                    +{remainingCount} more {remainingCount === 1 ? "member" : "members"}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="mt-2">
                        <Button
                            variant="ghost"
                            className="w-auto"
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/public-list/${list.id}`, "_blank", "noopener,noreferrer");
                            }}
                        >
                            View Public List
                        </Button>
                    </div>
                </div>

                {/* Right: Buttons */}
                <div className="w-full sm:w-1/4 flex flex-col justify-between sm:items-end items-center gap-2 sm:h-full">
                    <div className="flex gap-2 self-center sm:self-end">
                        {isAdmin && onEdit && (
                            <Button
                                variant="icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(list);
                                }}
                            >
                                <Icon icon="edit" />
                            </Button>
                        )}
                        {isAdmin && onDelete && (
                            <Button
                                variant="icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteListModalOpen(true);
                                }}
                            >
                                <Icon icon="delete" />
                            </Button>
                        )}
                        {isAdmin && (
                            <Button
                                variant="icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setInviteModalOpen(true);
                                }}
                            >
                                <Icon icon="invite" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Invite modal */}
            {isInviteModalOpen && (
                <Modal
                    isOpen={isInviteModalOpen}
                    size="small"
                    onClose={() => setInviteModalOpen(false)}
                >
                    <InviteUserForm
                        list={list}
                        onInvite={(email) => inviteUser(list.id, email)}
                        onClose={() => setInviteModalOpen(false)}
                    />
                </Modal>
            )}

            {/* Delete List Modal */}
            {deleteListModalOpen && (
                <Modal
                    isOpen={deleteListModalOpen}
                    size="small"
                    onClose={() => setDeleteListModalOpen(false)}
                >
                    <DeleteListForm
                        list={list}
                        onDelete={() => {
                            onDelete(list);
                            setDeleteListModalOpen(false);
                        }}
                        onClose={() => setDeleteListModalOpen(false)}
                    />
                </Modal>
            )}
        </>
    );
}
